import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Settings, Store, Truck, Phone, Users, Utensils, Monitor,
  Printer, QrCode, Plus, Trash2, Percent, Euro, ArrowLeft,
  CheckCircle2, XCircle, Edit, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '@/components/ui/back-button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * TenantSettingsManager - Einstellungen für Features und Rabatte
 * Ermöglicht Kunden, ihre eigenen Einstellungen zu verwalten
 */
export default function TenantSettingsManager({ onBack }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  
  // Neuer Rabatt Form
  const [newDiscount, setNewDiscount] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    value: 10,
    applies_to: 'pickup',
    auto_apply: true,
    min_order_value: null
  });

  // Lade Einstellungen
  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthToken = () => {
    return localStorage.getItem('access_token') || localStorage.getItem('auth_token') || localStorage.getItem('gastropos_token');
  };

  const fetchSettings = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/tenant-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        toast.error('Einstellungen konnten nicht geladen werden');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const updateFeatureToggle = async (key, value) => {
    setSaving(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/tenant-settings/features`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [key]: value })
      });

      if (response.ok) {
        const updated = await response.json();
        setSettings(updated);
        toast.success(`Einstellung ${value ? 'aktiviert' : 'deaktiviert'}`);
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error updating toggle:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const createDiscount = async () => {
    if (!newDiscount.name || newDiscount.value <= 0) {
      toast.error('Bitte Name und Wert eingeben');
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/tenant-settings/discounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDiscount)
      });

      if (response.ok) {
        toast.success(`Rabatt "${newDiscount.name}" erstellt`);
        setShowAddDiscount(false);
        setNewDiscount({
          name: '',
          description: '',
          discount_type: 'percentage',
          value: 10,
          applies_to: 'pickup',
          auto_apply: true,
          min_order_value: null
        });
        fetchSettings();
      } else {
        toast.error('Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Error creating discount:', error);
      toast.error('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const toggleDiscount = async (discountId, isActive) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/tenant-settings/discounts/${discountId}?is_active=${!isActive}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success(isActive ? 'Rabatt deaktiviert' : 'Rabatt aktiviert');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!window.confirm('Rabatt wirklich löschen?')) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/api/tenant-settings/discounts/${discountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Rabatt gelöscht');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const featureToggles = settings?.feature_toggles || {};
  const discounts = settings?.discounts || [];

  // Feature Toggle Konfigurationen
  const featureConfigs = [
    { key: 'counter_service_enabled', label: 'Thekenverkauf', icon: Store, description: 'Verkauf vor Ort an der Theke' },
    { key: 'pickup_enabled', label: 'Abholung', icon: Users, description: 'Kunden können Bestellungen abholen' },
    { key: 'delivery_enabled', label: 'Lieferung', icon: Truck, description: 'Lieferservice aktivieren' },
    { key: 'table_service_enabled', label: 'Tisch-Service', icon: Utensils, description: 'Tische verwalten und Bestellungen zuweisen' },
    { key: 'phone_orders_enabled', label: 'Telefon-Bestellungen', icon: Phone, description: 'Bestellungen per Telefon aufnehmen' },
    { key: 'online_ordering_enabled', label: 'Online-Bestellungen', icon: Monitor, description: 'Bestellungen aus Online-Kanälen' },
    { key: 'qr_ordering_enabled', label: 'QR-Bestellung', icon: QrCode, description: 'Gäste bestellen per QR-Code am Tisch' },
    { key: 'kitchen_display_enabled', label: 'Küchenmonitor', icon: Monitor, description: 'Bestellungen auf Küchenbildschirm anzeigen' },
    { key: 'auto_print_enabled', label: 'Auto-Druck', icon: Printer, description: 'Bons automatisch drucken' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <BackButton onClick={onBack} label="Zurück" />
        )}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="w-6 h-6 text-teal-400" />
            Geschäftseinstellungen
          </h1>
          <p className="text-gray-400 mt-1">Funktionen und Rabatte verwalten</p>
        </div>
      </div>

      {/* Feature Toggles */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Store className="w-5 h-5 text-amber-400" />
            Funktionen
          </CardTitle>
          <CardDescription>
            Aktivieren oder deaktivieren Sie Funktionen für Ihr Geschäft
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {featureConfigs.map((feature) => {
            const Icon = feature.icon;
            const isEnabled = featureToggles[feature.key] ?? true;
            
            return (
              <div
                key={feature.key}
                className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg hover:bg-gray-800/70 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${isEnabled ? 'bg-teal-500/20 text-teal-400' : 'bg-gray-700 text-gray-500'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{feature.label}</span>
                      {isEnabled ? (
                        <Badge variant="outline" className="text-xs border-teal-500/50 text-teal-400">
                          Aktiv
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-500">
                          Inaktiv
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{feature.description}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) => updateFeatureToggle(feature.key, checked)}
                  disabled={saving}
                  className="data-[state=checked]:bg-teal-500"
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Rabatte */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Percent className="w-5 h-5 text-emerald-400" />
                Rabatte
              </CardTitle>
              <CardDescription>
                Automatische Rabatte für Bestellungen
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowAddDiscount(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Rabatt
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {discounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Percent className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Keine Rabatte konfiguriert</p>
              <p className="text-sm">Erstellen Sie einen Rabatt, z.B. für Abholungen</p>
            </div>
          ) : (
            <div className="space-y-3">
              {discounts.map((discount) => (
                <div
                  key={discount.discount_id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    discount.is_active
                      ? 'bg-emerald-900/20 border-emerald-500/30'
                      : 'bg-gray-800/50 border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      discount.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-500'
                    }`}>
                      {discount.discount_type === 'percentage' ? (
                        <Percent className="w-5 h-5" />
                      ) : (
                        <Euro className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{discount.name}</span>
                        <Badge className={
                          discount.is_active
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50'
                            : 'bg-gray-700 text-gray-500'
                        }>
                          {discount.discount_type === 'percentage'
                            ? `${discount.value}%`
                            : `${discount.value}€`
                          }
                        </Badge>
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                          {discount.applies_to === 'pickup' && 'Nur Abholung'}
                          {discount.applies_to === 'delivery' && 'Nur Lieferung'}
                          {discount.applies_to === 'all' && 'Alle Bestellungen'}
                        </Badge>
                      </div>
                      {discount.description && (
                        <p className="text-sm text-gray-400">{discount.description}</p>
                      )}
                      {discount.auto_apply && (
                        <p className="text-xs text-teal-400 mt-1">✓ Automatisch angewandt</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={discount.is_active}
                      onCheckedChange={() => toggleDiscount(discount.discount_id, discount.is_active)}
                      className="data-[state=checked]:bg-emerald-500"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteDiscount(discount.discount_id)}
                      className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Neuer Rabatt Dialog */}
      <Dialog open={showAddDiscount} onOpenChange={setShowAddDiscount}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-emerald-400" />
              Neuen Rabatt erstellen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newDiscount.name}
                onChange={(e) => setNewDiscount({ ...newDiscount, name: e.target.value })}
                placeholder="z.B. Abholerrabatt"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Input
                value={newDiscount.description}
                onChange={(e) => setNewDiscount({ ...newDiscount, description: e.target.value })}
                placeholder="z.B. 10% Rabatt bei Abholung"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rabatt-Typ</Label>
                <Select
                  value={newDiscount.discount_type}
                  onValueChange={(v) => setNewDiscount({ ...newDiscount, discount_type: v })}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="percentage">Prozent (%)</SelectItem>
                    <SelectItem value="fixed">Fester Betrag (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Wert *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={newDiscount.value}
                    onChange={(e) => setNewDiscount({ ...newDiscount, value: parseFloat(e.target.value) || 0 })}
                    className="bg-gray-800 border-gray-700 pr-8"
                    min={0}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {newDiscount.discount_type === 'percentage' ? '%' : '€'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Gilt für</Label>
              <Select
                value={newDiscount.applies_to}
                onValueChange={(v) => setNewDiscount({ ...newDiscount, applies_to: v })}
              >
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="pickup">Nur Abholung</SelectItem>
                  <SelectItem value="delivery">Nur Lieferung</SelectItem>
                  <SelectItem value="all">Alle Bestellungen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Mindestbestellwert (optional)</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={newDiscount.min_order_value || ''}
                  onChange={(e) => setNewDiscount({ 
                    ...newDiscount, 
                    min_order_value: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="Kein Minimum"
                  className="bg-gray-800 border-gray-700 pr-8"
                  min={0}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
              <div>
                <span className="font-medium">Automatisch anwenden</span>
                <p className="text-sm text-gray-400">Rabatt wird automatisch bei passenden Bestellungen angewandt</p>
              </div>
              <Switch
                checked={newDiscount.auto_apply}
                onCheckedChange={(checked) => setNewDiscount({ ...newDiscount, auto_apply: checked })}
                className="data-[state=checked]:bg-teal-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDiscount(false)} className="border-gray-700">
              Abbrechen
            </Button>
            <Button
              onClick={createDiscount}
              disabled={saving || !newDiscount.name || newDiscount.value <= 0}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {saving ? 'Speichern...' : 'Rabatt erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
